import React, { useEffect, useState } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { api, money } from "../../lib/api";

const COLORS = ["#FACC15", "#10B981", "#3B82F6", "#F97316", "#A855F7", "#EC4899"];

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-[#121216] border border-white/10 rounded-lg p-5">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`font-heading font-black text-2xl lg:text-3xl mt-2 ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

function TaxSetting() {
  const [rate, setRate] = useState("");
  useEffect(() => {
    api.get("/settings/tax").then(({ data }) => setRate(String(data.rate))).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings/tax", { rate });
      setRate(String(data.rate));
      toast.success(`Sales tax set to ${data.rate}% for California orders`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save tax rate");
      api.get("/settings/tax").then(({ data }) => setRate(String(data.rate))).catch(() => {});
    }
  };
  return (
    <div data-testid="tax-setting" className="bg-[#121216] border border-white/10 rounded-lg px-5 py-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">CA Sales Tax (retail)</p>
      <div className="flex items-center gap-2 mt-1">
        <input data-testid="tax-rate-input" value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal"
          className="w-20 bg-[#0A0A0C] border border-white/15 rounded-sm px-2 py-1 text-white font-mono text-sm focus:outline-none focus:border-yellow-400" />
        <span className="text-zinc-400 font-mono text-sm">%</span>
        <button data-testid="tax-rate-save" onClick={save} className="btn-primary px-3 py-1 rounded-sm text-xs">Save</button>
      </div>
    </div>
  );
}

function WebhookSetting() {
  const [info, setInfo] = useState(null);
  const [key, setKey] = useState("");
  useEffect(() => {
    api.get("/admin/settings/square-webhook").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings/square-webhook", { signature_key: key });
      setInfo(data);
      setKey("");
      toast.success("Square webhook key saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save key"); }
  };
  if (!info) return null;
  return (
    <div data-testid="webhook-setting" className="bg-[#121216] border border-white/10 rounded-lg px-5 py-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
        Square Webhook · <span data-testid="webhook-status" className={info.configured ? "text-emerald-400" : "text-yellow-400"}>{info.configured ? "Connected" : "Not set"}</span>
      </p>
      <p className="font-mono text-[11px] text-zinc-400 mt-1 break-all">{info.webhook_url}</p>
      <div className="flex items-center gap-2 mt-2">
        <input data-testid="webhook-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={info.configured ? "Replace signature key" : "Paste signature key"}
          className="w-56 bg-[#0A0A0C] border border-white/15 rounded-sm px-2 py-1 text-white font-mono text-sm focus:outline-none focus:border-yellow-400" />
        <button data-testid="webhook-key-save" onClick={save} className="btn-primary px-3 py-1 rounded-sm text-xs">Save</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/dashboard").then(({ data }) => setD(data)).catch(() => {});
    api.get("/admin/analytics/breakdown").then(({ data }) => setB(data)).catch(() => {});
  }, []);

  if (!d) return <div className="text-zinc-500 font-mono py-32 text-center">Loading dashboard…</div>;

  const channelData = b ? [
    { name: "Retail", value: b.channel_units.retail },
    { name: "Wholesale", value: b.channel_units.wholesale },
  ] : [];

  return (
    <div className="px-5 lg:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-yellow-400 uppercase tracking-widest text-xs">Merchandise Dashboard</p>
          <h1 className="font-heading font-black uppercase text-4xl text-white mt-1">{d.round_name || "Overview"}</h1>
        </div>
        <div className="bg-[#121216] border border-yellow-400/30 rounded-lg px-5 py-3 text-right">
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Order Deadline</p>
          <p className="font-mono text-yellow-400 font-bold">NOV 1, 2026 · 11:59 PM PT · {d.days_remaining} days left</p>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-4"><TaxSetting /><WebhookSetting /></div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total Sales" value={money(d.total_sales)} accent="text-yellow-400" />
        <Kpi label="Total Orders" value={d.total_orders} />
        <Kpi label="Total Units" value={d.total_units} />
        <Kpi label="Total Customers" value={d.total_customers} />
        <Kpi label="Retail Sales" value={money(d.retail_sales)} />
        <Kpi label="Wholesale Sales" value={money(d.wholesale_sales)} accent="text-emerald-400" />
        <Kpi label="Retail Orders" value={d.retail_orders} />
        <Kpi label="Wholesale Orders" value={d.wholesale_orders} />
        <Kpi label="Academies Participating" value={d.academies_participating} />
        <Kpi label="Avg Retail Order" value={money(d.avg_retail_order)} />
        <Kpi label="Avg Wholesale Order" value={money(d.avg_wholesale_order)} />
        <Kpi label="Est. Merch Profit" value={money(d.estimated_profit)} accent="text-emerald-400" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-[#121216] border border-white/10 rounded-lg p-5">
          <h3 className="font-heading font-bold uppercase text-white mb-4">Units — Retail vs Wholesale</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={channelData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {channelData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#121216] border border-white/10 rounded-lg p-5">
          <h3 className="font-heading font-bold uppercase text-white mb-4">Top Categories (Revenue)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={(b?.by_category || []).slice(0, 6)}>
              <XAxis dataKey="name" stroke="#666" fontSize={11} />
              <YAxis stroke="#666" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #333" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="revenue" fill="#FACC15" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <p className="font-mono text-xs text-zinc-600 mt-6">Production cost & profit are internal. Retail customers and gym owners never see production cost.</p>
    </div>
  );
}
